import fuse from 'fuse.js';
import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';

import { styles } from '../../styles';

import {
  defaultExtractKey,
  defaultRenderOption,
  defaultSearchKeys,
} from './default';

const danger = css`
  ${styles.borderColor.danger};
  :hover,
  :focus,
  :active {
    ${styles.borderColor.danger}
  }
`;
const success = css`
  ${styles.borderColor.success};

  :hover,
  :focus,
  :active {
    ${styles.borderColor.success};
  }
`;

const sizes = {
  md: css`
    height: 41px;
    padding: 11px 14px;
  `,
  sm: css`
    padding: 1px 8px;
    ${styles.text.sm};
  `,
};

const Container = styled.div<{
  size: keyof typeof sizes;
  danger?: boolean;
  success?: boolean;
  search?: boolean;
}>`
  width: auto;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  white-space: nowrap;

  ${styles.border.md};
  ${styles.rounded.md};

  transition: all 0.3s;
  cursor: ${(props) => (props.search ? 'text' : 'pointer')};
  ${(props) => sizes[props.size]};

  ${(props) => (props.success ? success : null)};
  ${(props) => (props.danger ? danger : null)};

  :hover {
    ${styles.borderColor.primary}
  }
`;

const EmptyContainer = styled.div`
  ${styles.py.sm};
  ${styles.px.md};
  /* padding: ${(props) => props.theme.spaces.sm}
    ${(props) => props.theme.spaces.md}; */
  color: ${(props) => props.theme.color.border};
  ${styles.color.border};
`;

const SelectableOptionContainer = styled.div`
  cursor: pointer;
`;
const OptionList = styled.div`
  background-color: white;
  ${styles.shadow.base};
  ${styles.pt.sm}
  ${styles.pb.sm}
  ${styles.rounded.md}
  max-width: 500px;
  max-height: 500px;
  overflow: auto;
`;

const SearchInput = styled.input`
  border: none;
  padding: 0;
  outline: none;
  width: 100%;
`;

export type BaseSelectProps<Option> = {
  // placeholder?: string | JSX.Element;
  style?: CSSProperties;
  size?: keyof typeof sizes;

  danger?: boolean;
  success?: boolean;

  className?: string;
  // value: any;
  options?: Option[];
  extractKey?: (option: Option, index: number) => string | number;

  renderContent: (opt: { showSearchInput: boolean }) => ReactNode;
  renderContentAfter?: () => ReactNode;

  renderOption?: (
    option: Option,
    opt: { placeholder?: boolean; active?: boolean }
  ) => ReactNode;
  onSelect: (option: Option) => void;
  // onDeselect?: (option: Option) => void;
  // deselect?: string | JSX.Element;

  isOptionSelected: (option: Option) => boolean;

  allowSearch?: boolean;
  searchKeys?: string[];
  searchPlaceholder?: string;

  emptyOption?: string | JSX.Element | JSX.Element[] | null;

  closeOnSelect?: boolean;
};

export function BaseSelect<Option = { label?: string; value: string }>({
  style,
  className,
  size,
  renderContent,
  renderContentAfter,
  danger,
  success,
  options,

  extractKey = defaultExtractKey,
  renderOption = defaultRenderOption,
  isOptionSelected = () => false,

  onSelect,
  emptyOption = 'No option available',

  allowSearch,
  searchKeys = defaultSearchKeys,
  searchPlaceholder,

  closeOnSelect,
}: BaseSelectProps<Option>) {
  const [parentRef, setParentRef] = useState<HTMLElement | null>(null);
  const [popperRef, setPopperRef] = useState<HTMLElement | null>(null);
  const [show, setShow] = useState(false);
  const popper = usePopper(parentRef, popperRef, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [2, 5],
        },
      },
    ],
  });

  const [search, setSearch] = useState('');

  const fuzySearch = useMemo(() => {
    if (!allowSearch || !options) {
      return null;
    }
    return new fuse(options, {
      keys: searchKeys,
    });
  }, [options, allowSearch, searchKeys]);

  const filteredOptions = useMemo(() => {
    if (!allowSearch || search.length < 3) {
      return options;
    }
    const items = fuzySearch.search(search);
    return items.map((i) => i.item);
  }, [allowSearch, fuzySearch, options, search]);

  useEffect(() => {
    if (!show || !parentRef) {
      return;
    }
    const listener = (
      event: MouseEvent | CustomEvent<{ target: HTMLElement }>
    ) => {
      const target =
        event instanceof PointerEvent
          ? event.target
          : event instanceof CustomEvent
          ? (event.detail as { target: HTMLElement }).target
          : null;

      if (!target) {
        return;
      }

      if (
        parentRef &&
        parentRef !== target &&
        !parentRef.contains(target as HTMLElement)
      ) {
        setShow(false);
        setSearch('');
      }
    };
    window.addEventListener('click', listener);
    window.addEventListener('@react-ui/select-click', listener);
    return () => {
      window.removeEventListener('click', listener);
      window.removeEventListener('@react-ui/select-click', listener);
    };
  }, [parentRef, show]);

  const shouldShowSearchInput = show && allowSearch;

  return (
    <>
      <Container
        search={allowSearch}
        onClick={(e) => {
          if (!options) {
            return;
          }
          setShow(true);
          e.stopPropagation();
          window.dispatchEvent(
            new CustomEvent('@react-ui/select-click', {
              detail: { target: e.target },
            })
          );
        }}
        size={size || 'md'}
        className={className}
        ref={setParentRef}
        style={style}
        danger={danger}
        success={success}
      >
        {renderContent({
          showSearchInput: shouldShowSearchInput,
        })}
        {shouldShowSearchInput && (
          <SearchInput
            autoFocus
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
        {Boolean(renderContentAfter) && renderContentAfter()}

        {show &&
          ReactDOM.createPortal(
            <OptionList
              ref={setPopperRef}
              {...popper.attributes.popper}
              style={{
                ...(popper.styles.popper || null),
                minWidth: parentRef?.clientWidth,
              }}
            >
              {!filteredOptions ||
                (!filteredOptions.length && (
                  <EmptyContainer>{emptyOption}</EmptyContainer>
                ))}
              {filteredOptions?.map((opt, idx) => (
                <SelectableOptionContainer
                  key={extractKey(opt, idx)}
                  onClick={(e) => {
                    onSelect(opt);
                    if (closeOnSelect) {
                      setShow(false);
                    }
                    e.stopPropagation();
                  }}
                >
                  {renderOption(opt, {
                    active: isOptionSelected(opt),
                    placeholder: false,
                  })}
                </SelectableOptionContainer>
              ))}
            </OptionList>,
            document.body
          )}
      </Container>
    </>
  );
}
